import React, { useContext, useEffect, useState } from 'react';
import feeService from 'ApiService/Accountant/feeService';
import adminFeeService from 'ApiService/Admin/feeService';
import { AuthContext } from 'contexts/AuthContext';
import toast from 'react-hot-toast';
import AddInstallment from './AddInstallment';
import EditInstallment from './EditInstallment';
import ConfirmationModal from 'pages/Common/ConfirmationModal';

const StudentFeeDetails = ({
  students,
  selectedStudent,
  setSelectedStudent,
  loadingStudent,
}) => {
  const [editingInstallment, setEditingInstallment] = useState(null);
  const [addingInstallment, setAddingInstallment] = useState(false);
  const [selectedSwapId, setSelectedSwapId] = useState(null);
  const { state } = useContext(AuthContext);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [feeData, setFeeData] = useState(null);

  const handleEditClick = (installment, swapId = null) => {
    setEditingInstallment({ ...installment, swapId });
  };

  const fetchStudentDetails = async (studentId) => {
    console.log('Fetching student details for:', studentId);
    setIsLoadingDetails(true);

    try {
      const response = await feeService.getStudentFeeDetails(studentId);
      console.log('API Response:', response);

      if (response.data.success) {
        setFeeData(response.data.data);
      } else {
        console.error('API returned success: false', response.data);
        toast.error('Failed to fetch student details.');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error(
        error.response?.data?.message || 'Error fetching student details.'
      );
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleSelectChange = (event) => {
    const selectedId = event.target.value;
    const student = students.find((s) => s._id === selectedId);
    setSelectedStudent(student);
    setFeeData(null);
    setEditingInstallment(null);
    setAddingInstallment(false);
    setSelectedSwapId(null);
    if (student) {
      fetchStudentDetails(student._id);
    }
  };
  const handleSaveInstallment = async (
    installmentId,
    newDueDate,
    newAmount,
    swapId = null,
    course,
    batch
  ) => {
    try {
      const service =
        state.user.role === 'account' ? feeService : adminFeeService;

      const response = await service.updateInstallmentDate({
        installmentId: installmentId,
        newDueDate: newDueDate,
        amount: Number(newAmount),
        swapId,
        course,
        batch,
      });

      if (response.data.success) {
        await fetchStudentDetails(selectedStudent._id);
        setEditingInstallment(null);
        toast.success('Installment updated successfully.');
        return true;
      } else {
        throw new Error(
          response.data.message || 'Failed to update installment.'
        );
      }
    } catch (error) {
      toast.error(error.message || 'Something went wrong while updating.');
      return false;
    }
  };

  const handleAddInstallment = async (newInstallment) => {
    try {
      const service =
        state.user.role === 'account' ? feeService : adminFeeService;

      const payload = {
        studentId: selectedStudent._id,
        amount: newInstallment.amount,
        dueDate: newInstallment.dueDate,
        isSwapPayment: !!selectedSwapId,
        swapId: selectedSwapId,
        course: newInstallment.course,
        batch: newInstallment.batch,
      };

      const response = await service.addInstallment(payload);

      if (response.data.success) {
        await fetchStudentDetails(selectedStudent._id);
        setAddingInstallment(false);
        setSelectedSwapId(null);
        toast.success('Installment added successfully.');
        return true;
      } else {
        throw new Error(response.data.message || 'Failed to add installment.');
      }
    } catch (error) {
      toast.error(
        error.message || 'Something went wrong while adding installment.'
      );
      return false;
    }
  };
  const handleDeleteInstallment = async (
    installmentId,
    swapId = null,
    course,
    batch
  ) => {
    try {
      const service =
        state.user.role === 'account' ? feeService : adminFeeService;
      const response = await service.deleteInstallment(
        installmentId,
        swapId,
        course,
        batch
      );

      if (response.data.success) {
        await fetchStudentDetails(selectedStudent._id);
        toast.success('Installment deleted successfully.');
        return true;
      } else {
        throw new Error(
          response.data.message || 'Failed to delete installment.'
        );
      }
    } catch (error) {
      toast.error(error.message || 'Something went wrong while deleting.');
      return false;
    }
  };

  const calculateRemainingAmount = (feeSection) => {
    const pendingAmount = feeSection.pendingAmount;
    const pendingInstallmentsTotal = (feeSection.installments || [])
      .filter((inst) => !inst.isPaid)
      .reduce((sum, inst) => sum + inst.amount, 0);

    return pendingAmount - pendingInstallmentsTotal;
  };

  //remening inst
  const calculateAvailableForInstallments = (feeSection) => {
    const pendingAmount = feeSection.pendingAmount;
    const pendingInstallmentsTotal = (feeSection.installments || [])
      .filter((inst) => !inst.isPaid)
      .reduce((sum, inst) => sum + inst.amount, 0);

    return pendingAmount - pendingInstallmentsTotal;
  };

  return (
    <div className="w-full rounded-lg bg-white pt-6 dark:bg-navy-800">
      <h2 className="mb-6 text-xl font-bold text-gray-800 dark:text-white">
        Student Fee Details :
      </h2>

      <div className="w-full space-y-6">
        <div className="w-full">
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Select Student
          </label>
          <select
            onChange={handleSelectChange}
            value={selectedStudent ? selectedStudent._id : ''}
            className="w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:bg-navy-800"
          >
            <option value="">Select a Student</option>
            {loadingStudent ? (
              <option>Loading students...</option>
            ) : (
              students.map((student) => (
                <option key={student._id} value={student._id}>
                  {student.studentName}
                </option>
              ))
            )}
          </select>
        </div>

        {isLoadingDetails ? (
          <div className="py-4 text-center">Loading fee details...</div>
        ) : (
          feeData && (
            <div className="space-y-6" key={JSON.stringify(feeData)}>
              {/* Overall Summary */}
              <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:bg-navy-800">
                <h3 className="mb-4 text-lg font-semibold">
                  Overall Fee Summary
                </h3>
                <div className="mb-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Total Fee:
                    </span>
                    <span className="font-semibold text-gray-800 dark:text-white">
                      ₹{feeData.feeBreakdown.overall.totalAmount}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Total Paid:
                    </span>
                    <span className="font-semibold text-gray-800 dark:text-white">
                      ₹{feeData.feeBreakdown.overall.totalPaidAmount}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Total Pending:
                    </span>
                    <span className="font-semibold text-gray-800 dark:text-white">
                      ₹{feeData.feeBreakdown.overall.totalPendingAmount}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Payment Status:
                    </span>
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-medium ${
                        feeData.feeBreakdown.overall.isFullyPaid
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {feeData.feeBreakdown.overall.isFullyPaid
                        ? 'Paid in Full'
                        : 'Partially Paid'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Original Course Fee Section */}
              <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:bg-navy-800">
                <h3 className="mb-4 text-lg font-semibold">Original Course</h3>
                <div className="mb-4">
                  <div className="mb-2 text-sm font-medium text-gray-700">
                    {feeData.feeBreakdown.original.course.name} (Batch:{' '}
                    {feeData.feeBreakdown.original.batch.batchNumber})
                  </div>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                    <div>
                      <span className="block text-xs text-gray-500">
                        Total Amount
                      </span>
                      <span className="font-medium">
                        ₹{feeData.feeBreakdown.original.totalAmount}
                      </span>
                    </div>
                    <div>
                      <span className="block text-xs text-gray-500">
                        Paid Amount
                      </span>
                      <span className="font-medium">
                        ₹{feeData.feeBreakdown.original.paidAmount}
                      </span>
                    </div>
                    <div>
                      <span className="block text-xs text-gray-500">
                        Pending Amount
                      </span>
                      <span className="font-medium">
                        ₹{feeData.feeBreakdown.original.pendingAmount}
                      </span>
                    </div>
                  </div>
                </div>

                {feeData.feeBreakdown.original.isFullPayment ? (
                  <div className="flex items-center gap-2 rounded-lg bg-blue-50 p-4 text-sm text-blue-800">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>Full payment made for original course.</span>
                  </div>
                ) : (
                  <div>
                    <div className="mb-4 flex items-center justify-between">
                      <h4 className="text-sm font-medium text-gray-700">
                        Installments
                      </h4>

                      {!addingInstallment &&
                        calculateAvailableForInstallments(
                          feeData.feeBreakdown.original
                        ) > 0 && (
                          <div className="flex flex-col items-center justify-end gap-2 sm:flex-row">
                            <div className="text-sm text-blue-600">
                              <span className="font-medium">
                                Available for new installments:{' '}
                              </span>
                              <span>
                                ₹
                                {calculateAvailableForInstallments(
                                  feeData.feeBreakdown.original
                                )}
                              </span>
                            </div>
                            <button
                              onClick={() => {
                                setAddingInstallment(true);
                                setSelectedSwapId(null);
                              }}
                              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-200"
                            >
                              Add Installment
                            </button>
                          </div>
                        )}
                    </div>

                    <div className="rounded-lg">
                      {(feeData.feeBreakdown.original.installments || [])
                        .length === 0 ? (
                        <div className="text-center text-gray-500">
                          No installments added yet
                        </div>
                      ) : (
                        (feeData.feeBreakdown.original.installments || []).map(
                          (installment) => (
                            <div
                              key={installment._id}
                              className="mb-4 last:mb-0"
                            >
                              {editingInstallment &&
                              editingInstallment._id === installment._id &&
                              editingInstallment.swapId === null ? (
                                <EditInstallment
                                  installment={installment}
                                  onCancel={() => setEditingInstallment(null)}
                                  onSave={(id, date, amount) =>
                                    handleSaveInstallment(
                                      id,
                                      date,
                                      amount,
                                      null,
                                      installment.course,
                                      installment.batch
                                    )
                                  }
                                  remainingAmount={calculateRemainingAmount(
                                    feeData.feeBreakdown.original
                                  )}
                                />
                              ) : (
                                <InstallmentCard
                                  installment={installment}
                                  onEdit={() => handleEditClick(installment)}
                                  onDelete={() =>
                                    handleDeleteInstallment(
                                      installment._id,
                                      null,
                                      installment.course,
                                      installment.batch
                                    )
                                  }
                                />
                              )}
                            </div>
                          )
                        )
                      )}
                    </div>

                    {addingInstallment && selectedSwapId === null && (
                      <div className="mt-4">
                        <AddInstallment
                          studentId={selectedStudent._id}
                          onCancel={() => {
                            setAddingInstallment(false);
                          }}
                          onSave={handleAddInstallment}
                          remainingAmount={calculateAvailableForInstallments(
                            feeData.feeBreakdown.original
                          )}
                          existingInstallments={
                            feeData.feeBreakdown.original.installments || []
                          }
                          isSwapPayment={false}
                          course={feeData.feeBreakdown.original.course}
                          batch={feeData.feeBreakdown.original.batch}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Swap Courses Fee Sections */}
              {feeData.feeBreakdown.swaps &&
                feeData.feeBreakdown.swaps.length > 0 && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-semibold">Swap Courses</h3>

                    {feeData.feeBreakdown.swaps.map((swap, index) => (
                      <div
                        key={swap.swapId}
                        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:bg-navy-800"
                      >
                        <h4 className="text-md mb-4 font-semibold">
                          Swap #{index + 1}: {swap.swappedToCourse.name} (Batch:{' '}
                          {swap.swappedToBatch.batchNumber})
                        </h4>
                        <div className="mb-4">
                          <div className="mb-2 text-xs text-gray-500">
                            Swapped from {swap.swappedFromCourse.name} (Batch:{' '}
                            {swap.swappedFromBatch.batchNumber}) on{' '}
                            {new Date(swap.swapDate).toLocaleDateString()}
                          </div>
                          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                            <div>
                              <span className="block text-xs text-gray-500">
                                Total Amount
                              </span>
                              <span className="font-medium">
                                ₹{swap.totalAmount}
                              </span>
                            </div>
                            <div>
                              <span className="block text-xs text-gray-500">
                                Paid Amount
                              </span>
                              <span className="font-medium">
                                ₹{swap.paidAmount}
                              </span>
                            </div>
                            <div>
                              <span className="block text-xs text-gray-500">
                                Pending Amount
                              </span>
                              <span className="font-medium">
                                ₹{swap.pendingAmount}
                              </span>
                            </div>
                          </div>
                        </div>

                        {swap.isFullPayment ? (
                          <div className="flex items-center gap-2 rounded-lg bg-blue-50 p-4 text-sm text-blue-800">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <span>Full payment made for this swap.</span>
                          </div>
                        ) : (
                          <div>
                            <div className="mb-4 flex items-center justify-between">
                              <h5 className="text-sm font-medium text-gray-700">
                                Installments
                              </h5>

                              {!addingInstallment &&
                                calculateAvailableForInstallments(swap) > 0 && (
                                  <div className="flex flex-col items-center justify-end gap-2 sm:flex-row">
                                    <div className="text-sm text-blue-600">
                                      <span className="font-medium">
                                        Available for new installments:{' '}
                                      </span>
                                      <span>
                                        ₹
                                        {calculateAvailableForInstallments(
                                          swap
                                        )}
                                      </span>
                                    </div>
                                    <button
                                      onClick={() => {
                                        setAddingInstallment(true);
                                        setSelectedSwapId(swap.swapId);
                                      }}
                                      className="rounded-lg border border-blue-300 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-200"
                                    >
                                      Add Swap Installment
                                    </button>
                                  </div>
                                )}
                            </div>

                            <div className="rounded-lg">
                              {(swap.installments || []).length === 0 ? (
                                <div className="text-center text-gray-500">
                                  No installments added yet
                                </div>
                              ) : (
                                (swap.installments || []).map((installment) => (
                                  <div
                                    key={installment._id}
                                    className="mb-4 last:mb-0"
                                  >
                                    {editingInstallment &&
                                    editingInstallment._id ===
                                      installment._id &&
                                    editingInstallment.swapId ===
                                      swap.swapId ? (
                                      <EditInstallment
                                        installment={installment}
                                        onCancel={() =>
                                          setEditingInstallment(null)
                                        }
                                        onSave={(id, date, amount) =>
                                          handleSaveInstallment(
                                            id,
                                            date,
                                            amount,
                                            swap.swapId,
                                            installment.course,
                                            installment.batch
                                          )
                                        }
                                        remainingAmount={calculateRemainingAmount(
                                          swap
                                        )}
                                      />
                                    ) : (
                                      <InstallmentCard
                                        installment={{
                                          ...installment,
                                          isSwapPayment: true,
                                        }}
                                        onEdit={() =>
                                          handleEditClick(
                                            installment,
                                            swap.swapId
                                          )
                                        }
                                        onDelete={() =>
                                          handleDeleteInstallment(
                                            installment._id,
                                            swap.swapId,
                                            installment.course,
                                            installment.batch
                                          )
                                        }
                                      />
                                    )}
                                  </div>
                                ))
                              )}
                            </div>

                            {addingInstallment &&
                              selectedSwapId === swap.swapId && (
                                <div className="mt-4">
                                  <AddInstallment
                                    studentId={selectedStudent._id}
                                    onCancel={() => {
                                      setAddingInstallment(false);
                                      setSelectedSwapId(null);
                                    }}
                                    onSave={handleAddInstallment}
                                    remainingAmount={calculateAvailableForInstallments(
                                      swap
                                    )}
                                    existingInstallments={
                                      swap.installments || []
                                    }
                                    isSwapPayment={true}
                                    course={swap.swappedToCourse}
                                    batch={swap.swappedToBatch}
                                  />
                                </div>
                              )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
            </div>
          )
        )}
      </div>
    </div>
  );
};

const InstallmentCard = ({ installment, onEdit, onDelete }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  return (
    <>
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:bg-navy-800">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Amount:</span>
              <span className="text-gray-800 dark:text-white">
                ₹{installment.amount}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">
                Due Date:
              </span>
              <span className="text-gray-800 dark:text-white">
                {new Date(installment.dueDate).toLocaleDateString()}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Status:</span>
              <span
                className={`rounded-full px-2 py-1 text-xs font-medium ${
                  installment.isPaid
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {installment.isPaid ? 'Paid' : 'Pending'}
              </span>
              {installment.isSwapPayment && (
                <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                  Swap
                </span>
              )}
            </div>
          </div>
          {!installment.isPaid && (
            <div className="flex items-center gap-2">
              <button
                onClick={onEdit}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                Edit
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-200"
              >
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          onDelete();
          setShowDeleteConfirm(false);
        }}
        title="Delete Installment"
        message={`Are you sure you want to delete this installment of ₹${installment.amount}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        icon="warning"
      />
    </>
  );
};

export default StudentFeeDetails;
